import { AddIQCOrder, IQCOrder, OrderV2, QCStateEnum } from "@/types/inventoryV2";
import { Button, Flex, Modal, ModalProps, notification, Result, Spin, Steps } from "antd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddWrap from "./style";
import { addQcOrder, inventoryQcState, orderFetch } from "@/apis/inventoryv2";
import IQCList, { IQCListRef } from "../..";
import SelectOrderContent from "../../../../../../components/Selector/SelectOrderContent";
import AddIQCForm, { AddIQCFormRef } from "./AddIQCForm";

interface AddIQCProps extends Omit<ModalProps, "onClose"> {
  onClose: (order: IQCOrder | null) => void;
  order?: OrderV2;    //有order就是从出入库准备添加，否则是从列表添加
  handleQCState?: (val: QCStateEnum, orderId: string) => void;
};
const AddIQC: FC<AddIQCProps> = ({ onClose, order, handleQCState, ...modalProps }) => {
  const { t } = useTranslation();
  const formRef = useRef<AddIQCFormRef>(null);
  const listRef = useRef<IQCListRef>(null);
  const [inventoryOrder, setInventoryOrder] = useState<OrderV2 | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);
  const [adding, setAdding] = useState<boolean>(false);

  const [current, setCurrent] = useState<number>(0);
  const [selectOrderId, setSelectOrderId] = useState<string>('');
  const [iqcOrder, setIqcOrder] = useState<IQCOrder | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);


  useEffect(() => {
    if (order) {
      getOrderData(order.id)
    }
  }, [order]);

  const getOrderData = (id: string, next?: boolean) => {
    if (fetching) return
    setFetching(true);
    orderFetch(id)
      .then(res => {
        setInventoryOrder({ ...res });
        next && setCurrent(1);
      })
      .catch(err => notification.error({ message: t('warehouse.numberNotExist'), description: order?.number ?? '' }))
      .finally(() => setFetching(false))
  };

  const handleAdd = () => {
    if (adding) return
    let addInfo = formRef.current?.getSubmitData();
    if (addInfo) {
      setAdding(true);
      addQcOrder(addInfo)
        .then(res => {
          inventoryOrder && inventoryOrder.iqcState === null && inventoryQcState(inventoryOrder.id, QCStateEnum.QC_ING)
          inventoryOrder?.iqcState && [QCStateEnum.QC_PASS, QCStateEnum.QC_FAIL].includes(inventoryOrder.iqcState) && inventoryQcState(inventoryOrder.id, QCStateEnum.QC_ING)
          listRef.current?.addItem({ ...res })
          setIqcOrder({ ...res, sourceOrder: inventoryOrder, sourceOrderId: inventoryOrder?.id ?? '' });
          notification.success({ message: t('warehouse.doneTip', { number: res.number }) });
          setCurrent(2);
          setAddOpen(false)
        })
        .catch(err => notification.error({ message: t('warehouse.addErr1') }))
        .finally(() => setAdding(false))

    } else notification.error({ message: t('warehouse.addErr') })
  }

  //没有order，选择order后下一步
  const nextOne = () => {
    if (!!!selectOrderId) return notification.error({ message: t('warehouse.selectInventory') });
    getOrderData(selectOrderId, true);
  };

  const back = () => {
    if (current === 1) { setSelectOrderId(''); setInventoryOrder(null); }
    setCurrent(last => last - 1);
  }

  const openAdd = () => {
    setAddOpen(true);
  };


  const getFooter = useCallback(() => {
    if (!!order) return null;
    if (current === 0 && !!order) return null;
    if (current === 0 && !!!order) return (
      <Button onClick={nextOne} type="primary" >{t('warehouse.next')}</Button>
    )
    if (current === 1) return (
      <Flex gap={6}>
        <Button onClick={back} >{t('warehouse.previous')}</Button>
        <Button onClick={handleAdd} loading={adding} type="primary" >{t('warehouse.add')}</Button>
      </Flex>
    )
    if (current === 2) return null
  }, [current, order, nextOne]);

  const handleClose = () => {
    onClose(iqcOrder)
  };



  const firstStep = !!order ? {
    title: `${t('warehouse.qcOrder')}`,
    key: 0,
    content: <IQCList listRef={listRef} inventoryOrder={order} handleNext={openAdd} handleQCState={handleQCState} />
  } : {
    title: t('warehouse.selectInventory'),
    key: 0,
    content: <SelectOrderContent selectedIds={selectOrderId ? [selectOrderId] : []} onSelect={ids => setSelectOrderId(ids[0])} />
  }

  const steps = [
    { ...firstStep },
    {
      title: t('warehouse.addQc'),
      key: 1,
      content: <AddIQCForm order={inventoryOrder!} formRef={formRef} formType="ADD" />
    },
    {
      title: t('warehouse.done'),
      key: 2,
      content: <Result
        status="success"
        title={t("warehouse.done")}
        subTitle={t("warehouse.doneTip", { number: iqcOrder?.number ?? '' })}
      />
    }
  ]

  return (
    <Modal
      title={!!order ? `${order.number} ${t('warehouse.qcOrder')}` : t('warehouse.addQc')}
      onCancel={handleClose}
      footer={
        <Flex style={{ width: "100%" }} gap={8} >
          {getFooter()}
        </Flex>
      }
      width={1200}
      maskClosable={false}
      {...modalProps}
    >
      <Spin spinning={fetching || adding}>
        <AddWrap>
          {!!!order && <Steps current={current} items={steps} />}
          <div className="content">
            {
              !!!order ? steps[current].content
                : steps[0].content
            }
          </div>
        </AddWrap>
      </Spin>
      {
        addOpen && <Modal open={addOpen}
          title={t('warehouse.addQc')}
          onCancel={() => setAddOpen(false)}
          onOk={handleAdd}
          width={1200}
          okButtonProps={{ loading: adding }}
          maskClosable={false}
        >
          <AddWrap>
            <AddIQCForm order={inventoryOrder!} formRef={formRef} formType="ADD" />
          </AddWrap>
        </Modal>

      }
    </Modal>
  )
};

export default AddIQC;